import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import CustomDatePicker from "../../components/Common/CustomDatePicker";
import { useAuth } from "../../contexts/AuthContext";
import { useAuthRequired } from "../../contexts/AuthRequiredContext";
import { storeVenueBriefId } from "../../services/venueBriefService";
import { saveAuthReturnTo } from "../../utils/authReturnTo";
import { resolveUserDisplayName } from "../../utils/userDisplayName";
import { formatLocalCalendarYmd } from "../../components/VenueDashboard/utils/dateUtils";
import {
  FEATURED_VENUE_CUSTOM_PRICING_LABEL,
  FEATURED_VENUE_PRICE_FALLBACKS,
  fetchFeaturedVenueStats,
  KIJU_COFFEE_VENUE_ID,
} from "../../utils/featuredVenueDisplay";
import VenuesBanner from "../../components/VenuesBanner/VenuesBanner";
import CloseButton from "../../components/Auth/shared/CloseButton";
import AskHelpButton from "../../components/Comms/AskHelpButton";
import { ReactComponent as OccasiaLogoIcon } from "../../components/icons/occasia-logo-icon.svg";
import { FigmaChevronDownIcon } from "../../components/icons";
import { API_BASE_URL } from "../../config/apiEndpoints";
import "./ManualBooking.css";

// Vibe and must-haves are asked in the brief form, so there are no follow-up questions.
const QUESTION_IDS = [];
const QUESTION_COUNT = QUESTION_IDS.length;
const MANDATORY_QUESTION_IDS = new Set();

const EVENT_TYPES = ["Supper club", "Workshop", "Creative meetup", "Private gathering", "Community event", "Other"];
const LOCATION_AREAS = [
  "Barking and Dagenham",
  "Barnet",
  "Bexley",
  "Brent",
  "Bromley",
  "Camden",
  "City of London",
  "Croydon",
  "Ealing",
  "Enfield",
  "Greenwich",
  "Hackney",
  "Hammersmith and Fulham",
  "Haringey",
  "Harrow",
  "Havering",
  "Hillingdon",
  "Hounslow",
  "Islington",
  "Kensington and Chelsea",
  "Kingston upon Thames",
  "Lambeth",
  "Lewisham",
  "Merton",
  "Newham",
  "Redbridge",
  "Richmond upon Thames",
  "Southwark",
  "Sutton",
  "Tower Hamlets",
  "Waltham Forest",
  "Wandsworth",
  "Westminster",
];
const RECURRING_OPTIONS = ["One-off", "Recurring", "Not sure yet"];
const FLEXIBLE_DATE_OPTIONS = ["Fixed date", "Within a week", "Within a month"];
const BOOKING_TIME_SLOTS = ["Morning", "Afternoon", "Evening"];
const DURATION_OPTIONS = [
  { label: "2 hours", value: "2 hours" },
  { label: "2 - 4 hours", value: "2 - 4 hours" },
  { label: "Half day", sub: "4 hours", value: "Half day (4 hours)" },
  { label: "Full day", sub: "9 hours", value: "Full day (9 hours)" },
];
const VIBE_OPTIONS = ["Industrial / raw", "Light & airy", "Minimal & quiet", "Warm & intimate", "Open to anything"];
const SPACE_TYPES = ["Cafe", "Meeting room", "Pub function room", "Restaurant / private dining", "Rooftop", "Studio", "Open to anything"];
const MUST_HAVE_OPTIONS = [
  "AV / projector",
  "Catering on site",
  "Breakout area",
  "Natural light",
  "Parking",
  "Restaurant/Outdoor space",
  "Wheelchair access",
  "No specific need",
];
const BUDGET_OPTIONS = ["Under £200", "£200 - £500", "£500 - £1,000", "£1,000 - £2,500", "Over £2,500"];
const CONTACT_METHOD_OPTIONS = ["Email", "Whatsapp", "Either works"];

const VENUE_COUNTS = [140, 100, 80, 60, 24];

const ICON = (name) => `/manual-booking/icons/${name}`;

const HOW_IT_WORKS_COMPARE = [
  {
    title: "Average time to confirmed venue",
    traditionalCopy: "Manual sourcing takes days per event.",
    nowCopy: "Occasia sources a confirmed venue in 24–48 hrs.",
    traditionalValue: "Multiple days",
    nowValue: "24–48 hrs",
    traditionalWidth: "78%",
    nowWidth: "22%",
  },
  {
    title: "Less back-and-forth than sourcing manually",
    traditionalCopy: "Manual sourcing means chasing venues across inboxes.",
    nowCopy: "70% less back-and-forth. One place for your brief, proposals, and booking.",
    traditionalValue: "Lots of emails",
    nowValue: "1 place",
    traditionalWidth: "78%",
    nowWidth: "22%",
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    icon: ICON("how-clipboard-dark.png"),
    title: "Your brief. Two minutes.",
    desc: (
      <>
        Event type, date, guest count, area.
        <br /> That is all we need to start.
      </>
    ),
    pill: "No account needed to submit a request",
  },
  {
    icon: ICON("how-building-dark.png"),
    title: "We confirm the venue.",
    desc: (
      <>
        We match, approach, and negotiate.
        <br /> Availability confirmed within 24–48 hours.
      </>
    ),
    pill: "Your data is never sold or shared",
  },
  {
    icon: ICON("how-card-dark.png"),
    title: "One payment. One place.",
    desc: "Booking secured. All venue communication in a single thread. Nothing in your inbox.",
    pill: (
      <>
        Payments secured by <img src={ICON("stripe-logo.png")} alt="Stripe" className="mb-trust-stripe" />
      </>
    ),
  },
];

const HERO_VENUE_PHOTOS = [
  "/hero-strip/hero-1.jpg",
  "/hero-strip/hero-2.jpg",
  "/hero-strip/hero-3.jpg",
  "/hero-strip/hero-4.jpg",
  "/hero-strip/hero-5.jpg",
  "/hero-strip/hero-6.jpg",
];

const HERO_TITLE_LINES = [
  { words: ["Stop", "hunting", "for", "venues,"], muted: true },
  { words: ["Start", "running", "events."], muted: false },
];

const HERO_TITLE_WORD_START = 0.12;
const HERO_TITLE_WORD_STAGGER = 0.1;
const BUILT_FOR_SCENES = [
  {
    event: "Networking event",
    image: "/manual-booking/scenes/networking-event.jpg",
    eyebrow: "Pub function room",
    name: "Founders networking event",
  },
  {
    event: "Creative social",
    image: "/manual-booking/scenes/sip-and-paint.jpg",
    eyebrow: "Cocktail bar",
    name: "Sip and paint event",
  },
  {
    event: "Professional workshop",
    image: "/manual-booking/scenes/legal-ai-workshop.jpg",
    eyebrow: "Meeting room in co-working space",
    name: "Legal AI workshop",
  },
  {
    event: "Private meet-up",
    image: "/manual-booking/scenes/pub-quiz.jpg",
    eyebrow: "Pub semi-private area",
    name: "Pub quiz",
  },
];
const BUILT_FOR_EVENT_TYPES = [
  "Creative workshops & Classes",
  "Book Clubs",
  "Community Meetups",
  "Launch Nights",
  "Birthday & Milestone Gatherings",
  "Quiz and Games Nights",
  "Wellness Sessions",
  "Panel Events",
  "Team Socials",
  "Networking Evenings",
];
const HERO_IMAGE_START = 1.05;
const HERO_CARD_STAGGER = 0.08;
const HERO_CARD_IN_DURATION = 0.6;
let heroMobileMarqueeInterval = 0;

const FEATURED_VENUES = [
  {
    id: "660e8400-e29b-41d4-a716-446655440007",
    name: "Kasa Café",
    image: "/landing2/top-picks/kasa.png",
    address: "150 New Cavendish St, London",
    transit: "5 min from Goodge Street",
    tags: ["Cozy", "Modern", "Pet friendly"],
    path: "/venue/kasa-cafe",
  },
  {
    id: "660e8400-e29b-41d4-a716-446655440023",
    name: "Kiju Café",
    image: "/landing2/top-picks/kiju.png",
    address: "V12, Railway Arches, Patcham Terrace, Nine Elms, London",
    transit: "4 min from Nine Elms",
    tags: ["Cozy", "Modern", "Pet friendly"],
    path: "/venue/kiju-coffee",
  },
];

const HOMEPAGE_FAQS = [
  {
    q: "Is Occasia free to use?",
    a: "Yes! Browsing, searching, and saving venues is completely free. You only pay when you confirm a booking.",
  },
  {
    q: "What types of events can I book for?",
    a: "Anything from dinner parties and birthday celebrations to corporate off-sites, workshops, and community meetups.",
  },
  {
    q: "How does pricing work?",
    a: "Each venue sets their own pricing. We show you the full cost upfront — no hidden fees or surprises.",
  },
  {
    q: "Can I cancel a booking?",
    a: "Cancellation policies vary by venue. You'll always see the policy before you book.",
  },
];

const TESTIMONIAL_GRID_GAP = 22;
const TESTIMONIAL_GRID_RADIUS = 3.2;
const TESTIMONIAL_GRID_BURST_MS = 200;
const TESTIMONIAL_GRID_HOLD = 1.05;
const TESTIMONIAL_GRID_RING = [
  { max: 0.85, color: "#046242" },
  { max: 1.85, color: "#BED347" },
  { max: TESTIMONIAL_GRID_RADIUS, color: "#E4FD58" },
];

const TESTIMONIALS = [
  {
    event: "Painting workshop",
    quote:
      "The cafe owner is the nicest person. I made some content and we agreed to make collaborations on social media. Couldn't be happier with the venue and thankful to have you support us.",
    author: "Hanna",
    avatar: "/manual-booking/testimonials/hanna.jpg",
    avatarPosition: "52% 28%",
  },
  {
    event: "Workshop and networking event",
    quote:
      "The event went well and the venue was great. Everything went well, the staff were helpful and friendly. The venue, clean, easy to use and we were very happy. Thank you again for all of your help. I haven't been able to say enough good things about our experience with you and have recommended you to people.",
    author: "Helena",
    avatar: "/manual-booking/testimonials/helena.jpg",
    avatarPosition: "50% 20%",
  },
  {
    event: "Personal branding session",
    quote:
      "It was great to collaborate with you as our venue partner for this event. We would love to continue working together for our future events.",
    author: "Shreya",
    avatar: "/manual-booking/testimonials/shreya.jpg",
    avatarPosition: "48% 22%",
  },
];

const INITIAL_STATE = {
  planningDescription: "",
  planningType: "",
  recurringPreference: "",
  firstDate: "",
  flexibleDate: "",
  guestCount: "",
  preferredArea: [],
  bookingTimeSlot: "",
  duration: "",
  spaceTypes: [],
  vibes: [],
  mustHaves: [],
  budgetRange: "",
  pricingModel: "Not sure",
  additionalInfo: "",
  name: "",
  email: "",
  phone: "",
  contactMethod: "",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The phone field sits after a fixed +44 prefix, so keep only the UK local part. */
const toBriefPhoneValue = (raw) => {
  const value = String(raw || "").trim();
  if (!value || (value.startsWith("+") && !value.startsWith("+44"))) return "";
  return value.replace(/^\+?44\s*/, "").replace(/^0/, "").trim();
};

const organiserContactFromAccount = (currentUser, userProfile) => ({
  name: resolveUserDisplayName(userProfile, currentUser),
  email: String(currentUser?.email || userProfile?.email || "").trim(),
  phone: toBriefPhoneValue(userProfile?.phone || userProfile?.phone_number || currentUser?.phoneNumber),
});
const OPEN_TO_ANYTHING = "Open to anything";

const normalizeMultiSelect = (values, allowedValues) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  if (values.includes(OPEN_TO_ANYTHING)) return [];
  return values.filter((item) => allowedValues.includes(item));
};

const isValidPhone = (phone) => {
  const t = String(phone || "").trim();
  if (!t) return true;
  const clean = t.replace(/[\s\-()]/g, "");
  const digits = clean.startsWith("+") ? clean.slice(1) : clean;
  if (!/^\d+$/.test(digits)) return false;
  return digits.length >= 9 && digits.length <= 15;
};

const formatDateLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const formatRecurringLabel = (value) => {
  if (value === "One-off") return "One-off booking";
  if (value === "Recurring") return "Recurring booking";
  return value;
};

const mapContactMethodToApi = (method) => {
  if (method === "Whatsapp") return "WhatsApp";
  return method;
};

const mapDateFlexibilityToApi = (value) => {
  const map = {
    "Fixed date": null,
    "Within a week": "Yes, within a week",
    "Within a month": "Yes within a month",
  };
  return map[value] ?? null;
};

const mapBookingFrequencyToApi = (value) => {
  const map = {
    "One-off": "One-off",
    Recurring: "Not sure yet",
    "Not sure yet": "Not sure yet",
  };
  return map[value] ?? null;
};

const mapDurationToApi = (value) => {
  const map = {
    "2 hours": "Up to 2 hours",
    "2 - 4 hours": "3-4 hours",
    "Half day (4 hours)": "Half day(4-6 hours)",
    "Full day (9 hours)": "Full day",
  };
  return map[value] ?? null;
};

const mapBudgetToApi = (value) => {
  const map = {
    "Under £200": "£100-£250",
    "£200 - £500": "£250-£500",
    "£500 - £1,000": "£500-£1,000",
    "£1,000 - £2,500": "Over £1,000",
    "Over £2,500": "Over £1,000",
  };
  return map[value] ?? null;
};

const mapSpaceTypeToApi = (label) => {
  const map = {
    Cafe: "Cafe or coffee shop",
    "Meeting room": "Community space",
    "Pub function room": "Pub or bar with private space",
    "Restaurant / private dining": "Cafe or coffee shop",
    Rooftop: "Gallery or creative space",
    Studio: "Studio",
    [OPEN_TO_ANYTHING]: OPEN_TO_ANYTHING,
  };
  return map[label] || label;
};

const mapVibeToApi = (label) => {
  const map = {
    "Industrial / raw": "Industrial or raw",
    "Light & airy": "Light and airy",
    "Minimal & quiet": "Minimal and quiet",
    "Warm & intimate": "Warm and intimate",
    [OPEN_TO_ANYTHING]: OPEN_TO_ANYTHING,
  };
  return map[label] || label;
};

const isStep1Complete = (fs) =>
  Boolean(
    fs.planningType &&
      fs.recurringPreference &&
      fs.firstDate &&
      fs.flexibleDate &&
      fs.guestCount &&
      Number.parseInt(fs.guestCount, 10) >= 1 &&
      fs.preferredArea.length > 0 &&
      fs.bookingTimeSlot &&
      fs.duration
  );

const isStep2Complete = (fs) =>
  Boolean(
    fs.budgetRange &&
      fs.vibes.length > 0 &&
      fs.spaceTypes.length > 0 &&
      fs.additionalInfo.trim()
  );

const isStep3Complete = (fs) =>
  Boolean(
    fs.name.trim() &&
      fs.email.trim() &&
      EMAIL_REGEX.test(fs.email.trim()) &&
      isValidPhone(fs.phone) &&
      fs.contactMethod
  );

const BubbleIcon = () => (
  <svg className="mb-bubble-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <circle cx="6" cy="10" r="3" fill="#046242" opacity="0.35" />
    <circle cx="11" cy="7" r="4" fill="#046242" opacity="0.55" />
    <circle cx="14" cy="12" r="2.5" fill="#046242" opacity="0.75" />
  </svg>
);

const AddIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const StarFeatureIcon = () => (
  <img
    className="mb-account-feature-icon"
    src="/manual-booking/icons/feature-sparkle.png"
    alt=""
    aria-hidden
    width="12"
    height="12"
  />
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StepStatusIcon = ({ state }) => (
  <span className={`mb-step-status-icon mb-step-status-icon--${state}`} aria-hidden>
    {state === "done" ? <CheckIcon /> : null}
  </span>
);

const Stepper = ({ currentStep }) => (
  <div className="mb-stepper" aria-label="Form progress">
    <div
      className={`mb-stepper-item ${currentStep > 1 ? "mb-stepper-item--done" : currentStep === 1 ? "mb-stepper-item--active" : ""}`}
    >
      <StepStatusIcon state={currentStep > 1 ? "done" : "active"} />
      Step 1
    </div>
    <span className="mb-stepper-line" aria-hidden />
    <div
      className={`mb-stepper-item ${
        currentStep > 2 ? "mb-stepper-item--done" : currentStep === 2 ? "mb-stepper-item--active" : "mb-stepper-item--pending"
      }`}
    >
      <StepStatusIcon state={currentStep > 2 ? "done" : currentStep === 2 ? "active" : "pending"} />
      Step 2
    </div>
    <span className="mb-stepper-line" aria-hidden />
    <div className={`mb-stepper-item ${currentStep >= 3 ? "mb-stepper-item--active" : "mb-stepper-item--pending"}`}>
      <StepStatusIcon state={currentStep >= 3 ? "active" : "pending"} />
      Step 3
    </div>
  </div>
);

const FieldLabel = ({ children, htmlFor }) => (
  <label className="mb-field-label" htmlFor={htmlFor} id={htmlFor ? `${htmlFor}-label` : undefined}>
    {children}
    <span className="required"> *</span>
  </label>
);

const ChipRemoveIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PendingIcon = ({ name }) => (
  <img className="mb-pending-icon" src={`/manual-booking/icons/pending-${name}.png`} alt="" aria-hidden width="20" height="20" />
);

const CONFIRM_WINDOW_MS = 24 * 60 * 60 * 1000;

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

const BriefCountdown = ({ startedAt }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = (startedAt ?? now) + CONFIRM_WINDOW_MS - now;
  return (
    <p className="mb-pending-countdown">
      Confirming in <strong>{formatCountdown(remaining)}</strong>
    </p>
  );
};

const BudgetSlider = ({ id, label, note, value, onChange, describedBy }) => {
  const index = BUDGET_OPTIONS.indexOf(value);
  const position = index < 0 ? 0 : index;
  const max = BUDGET_OPTIONS.length - 1;
  const commit = (i) => onChange(BUDGET_OPTIONS[Number(i)]);

  return (
    <div className={`mb-budget-slider ${index < 0 ? "mb-budget-slider--empty" : ""}`}>
      <div className="mb-budget-slider-head">
        {label}
        <span className="mb-budget-slider-value" aria-hidden>
          {index < 0 ? "Drag to choose a range" : value}
        </span>
      </div>
      <div className="mb-budget-slider-track">
        <div
          className="mb-budget-slider-bar"
          style={{ "--mb-budget-fill": `${(position / max) * 100}%` }}
          aria-hidden
        >
          {BUDGET_OPTIONS.map((option, step) => (
            step === 0 || step === max ? null : (
              <span
                key={option}
                className="mb-budget-slider-tick"
                style={{ left: `calc(10px + ${step} * (100% - 20px) / ${max})` }}
              />
            )
          ))}
        </div>
        <input
          id={id}
          type="range"
          className="mb-budget-slider-input"
          min={0}
          max={max}
          step={1}
          value={position}
          onChange={(e) => commit(e.target.value)}
          onPointerUp={(e) => commit(e.currentTarget.value)}
          onKeyUp={(e) => commit(e.currentTarget.value)}
          aria-valuetext={index < 0 ? "Not selected" : value}
          aria-describedby={describedBy}
        />
      </div>
      <div className="mb-budget-slider-ends" aria-hidden>
        <span>{BUDGET_OPTIONS[0]}</span>
        <span>{BUDGET_OPTIONS[max]}</span>
      </div>
      {note}
    </div>
  );
};

const MOBILE_SHEET_QUERY = "(max-width: 767px)";

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
};

const BriefSelect = ({ id, label, value, options, onChange, placeholder = "Select", multiple = false, describedBy }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const isSheet = useMediaQuery(MOBILE_SHEET_QUERY);
  const selected = multiple ? (Array.isArray(value) ? value : []) : value;
  const isEmpty = multiple ? selected.length === 0 : !selected;

  const placeMenu = useCallback(() => {
    if (isSheet) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuHeight = Math.min(280, options.length * 44 + 16);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    setMenuStyle({
      top: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [options.length, isSheet]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeMenu();
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
    };
    const onPointerDown = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", placeMenu, true);
    window.addEventListener("resize", placeMenu);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", placeMenu, true);
      window.removeEventListener("resize", placeMenu);
    };
  }, [open, placeMenu, value]);

  const choose = (option) => {
    if (!multiple) {
      onChange(option);
      setOpen(false);
      return;
    }
    onChange(selected.includes(option) ? selected.filter((item) => item !== option) : [...selected, option]);
  };

  const renderOptions = () =>
    options.map((option) => {
      const isSelected = multiple ? selected.includes(option) : option === selected;
      return (
        <button
          key={option}
          type="button"
          role="option"
          aria-selected={isSelected}
          className={`mb-select-menu-option${isSelected ? " mb-select-menu-option--selected" : ""}`}
          onClick={() => choose(option)}
        >
          {option}
          {multiple ? (
            <span className={`mb-select-menu-check${isSelected ? "" : " mb-select-menu-check--empty"}`} aria-hidden>
              {isSelected ? <CheckIcon /> : null}
            </span>
          ) : null}
        </button>
      );
    });

  return (
    <div className="mb-select-wrap">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`mb-select mb-select-trigger${isEmpty ? " mb-select--placeholder" : ""}${open ? " mb-select--open" : ""}${multiple ? " mb-select-trigger--chips" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-describedby={describedBy}
        aria-labelledby={`${id}-label`}
        onClick={() => setOpen((current) => !current)}
      >
        {multiple && !isEmpty ? (
          <span className="mb-select-chips">
            {selected.map((option) => (
              <span key={option} className="mb-select-chip">
                {option}
                <span
                  role="button"
                  tabIndex={0}
                  className="mb-select-chip-remove"
                  aria-label={`Remove ${option}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(selected.filter((item) => item !== option));
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    onChange(selected.filter((item) => item !== option));
                  }}
                >
                  <ChipRemoveIcon />
                </span>
              </span>
            ))}
          </span>
        ) : multiple ? (
          placeholder
        ) : (
          selected || placeholder
        )}
      </button>
      <ChevronDown className={open ? "mb-field-chevron--open" : ""} />
      {open
        ? createPortal(
            isSheet ? (
              <>
                <div className="mb-sheet-backdrop" aria-hidden onClick={() => setOpen(false)} />
                <div ref={menuRef} className="mb-sheet" role="dialog" aria-modal="true" aria-label={label}>
                  <div className="mb-sheet-handle" aria-hidden />
                  <div className="mb-sheet-head">
                    <p className="mb-sheet-title">{label}</p>
                    <button type="button" className="mb-sheet-close" aria-label="Close" onClick={() => setOpen(false)}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    </button>
                  </div>
                  <div
                    className="mb-sheet-list"
                    role="listbox"
                    aria-multiselectable={multiple || undefined}
                    aria-label={label}
                  >
                    {renderOptions()}
                  </div>
                  {multiple ? (
                    <div className="mb-sheet-footer">
                      <button type="button" className="mb-sheet-add" onClick={() => setOpen(false)}>
                        Add
                      </button>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div
                ref={menuRef}
                className="mb-select-menu"
                style={menuStyle}
                role="listbox"
                aria-multiselectable={multiple || undefined}
                aria-label={label}
              >
                {renderOptions()}
              </div>
            ),
            document.body
          )
        : null}
    </div>
  );
};

const GuestMinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const GuestPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChevronDown = ({ className = "" }) => (
  <FigmaChevronDownIcon className={`mb-field-chevron ${className}`} aria-hidden="true" />
);

const FigmaRadio = ({ label, checked, onChange }) => (
  <button type="button" className="mb-figma-radio" onClick={onChange} aria-pressed={checked}>
    <span className={`mb-figma-radio-dot ${checked ? "mb-figma-radio-dot--checked" : ""}`} />
    <span className="mb-figma-radio-label">{label}</span>
  </button>
);

const FigmaCheckbox = ({ label, sub, checked, onChange }) => (
  <button type="button" className="mb-figma-checkbox" onClick={onChange} aria-pressed={checked}>
    <span className={`mb-figma-checkbox-box ${checked ? "mb-figma-checkbox-box--checked" : ""}`}>
      {checked ? <CheckIcon /> : null}
    </span>
    <span className="mb-figma-checkbox-label">
      {label}
      {sub ? <span className="mb-figma-checkbox-sub"> ({sub})</span> : null}
    </span>
  </button>
);

const useGlowingDotGrid = (canvasRef, active) => {
  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    const wrap = canvas?.parentElement;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const clusters = [];
    let cols = 0;
    let rows = 0;
    let frameId = 0;
    let lastBurst = 0;

    const spawnBurst = () => {
      const count = 3 + Math.floor(Math.random() * 2);
      const margin = Math.ceil(TESTIMONIAL_GRID_RADIUS) + 1;
      for (let i = 0; i < count; i += 1) {
        clusters.push({
          col: margin + Math.floor(Math.random() * Math.max(1, cols - margin * 2)),
          row: margin + Math.floor(Math.random() * Math.max(1, rows - margin * 2)),
          born: performance.now(),
        });
      }
    };

    const ringColor = (distance) => {
      const ring = TESTIMONIAL_GRID_RING.find((item) => distance <= item.max);
      return ring?.color ?? "#E4FD58";
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / TESTIMONIAL_GRID_GAP);
      rows = Math.ceil(height / TESTIMONIAL_GRID_GAP);
      clusters.length = 0;
      lastBurst = 0;
    };

    const draw = (now) => {
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      ctx.clearRect(0, 0, width, height);

      if (now - lastBurst >= TESTIMONIAL_GRID_BURST_MS) {
        spawnBurst();
        lastBurst = now;
      }

      const hold = TESTIMONIAL_GRID_HOLD * 1000;
      for (let i = clusters.length - 1; i >= 0; i -= 1) {
        if (now - clusters[i].born >= hold) clusters.splice(i, 1);
      }

      const lit = new Map();
      clusters.forEach((cluster) => {
        const intensity = Math.sin(((now - cluster.born) / hold) * Math.PI);
        const extent = Math.ceil(TESTIMONIAL_GRID_RADIUS);
        for (let dy = -extent; dy <= extent; dy += 1) {
          for (let dx = -extent; dx <= extent; dx += 1) {
            const distance = Math.hypot(dx, dy);
            if (distance > TESTIMONIAL_GRID_RADIUS) continue;
            const key = `${cluster.col + dx},${cluster.row + dy}`;
            const prev = lit.get(key);
            if (!prev || intensity > prev.intensity) {
              lit.set(key, { color: ringColor(distance), intensity });
            }
          }
        }
      });

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const litDot = lit.get(`${col},${row}`);
          ctx.beginPath();
          ctx.arc(
            col * TESTIMONIAL_GRID_GAP,
            row * TESTIMONIAL_GRID_GAP,
            1.05,
            0,
            Math.PI * 2
          );
          if (litDot) {
            ctx.globalAlpha = 0.08 + litDot.intensity * 0.92;
            ctx.fillStyle = litDot.color;
          } else {
            ctx.globalAlpha = 1;
            ctx.fillStyle = "rgba(17, 17, 17, 0.07)";
          }
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      frameId = window.requestAnimationFrame(draw);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);
    frameId = window.requestAnimationFrame(draw);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
    };
  }, [canvasRef, active]);
};

const ManualBooking = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const { triggerAuthModal } = useAuthRequired();
  const [phase, setPhase] = useState("form");
  const [formStep, setFormStep] = useState(1);
  const [formState, setFormState] = useState(INITIAL_STATE);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedBriefId, setSubmittedBriefId] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [featuredVenueStats, setFeaturedVenueStats] = useState({});
  const [heroMarquee, setHeroMarquee] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefScrolled, setBriefScrolled] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [builtForScene, setBuiltForScene] = useState(0);
  const builtForRef = useRef(null);
  const builtForPillsRef = useRef(null);
  const heroStripRef = useRef(null);
  const heroStripGroupRef = useRef(null);
  const testimonialsGridRef = useRef(null);
  const briefGridRef = useRef(null);
  const outreachGridRef = useRef(null);
  const howCompareRef = useRef(null);
  const howPlaceRef = useRef(null);

  // Fill contact details from the organiser's account without overwriting anything they typed
  useEffect(() => {
    if (!currentUser) return;
    const contact = organiserContactFromAccount(currentUser, userProfile);
    setFormState((prev) => ({
      ...prev,
      name: prev.name.trim() ? prev.name : contact.name,
      email: prev.email.trim() ? prev.email : contact.email,
      phone: prev.phone.trim() ? prev.phone : contact.phone,
    }));
  }, [currentUser, userProfile]);

  useGlowingDotGrid(testimonialsGridRef, phase === "form");
  useGlowingDotGrid(briefGridRef, briefOpen);
  useGlowingDotGrid(outreachGridRef, phase === "questionnaire" || phase === "complete");

  useEffect(() => {
    HERO_VENUE_PHOTOS.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    BUILT_FOR_SCENES.forEach((scene) => {
      const img = new Image();
      img.src = scene.image;
    });
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setBuiltForScene((index) => (index + 1) % BUILT_FOR_SCENES.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [builtForScene]);

  useLayoutEffect(() => {
    const pills = builtForPillsRef.current;
    const stack = pills?.closest(".mb-built-for-stack");
    if (!pills || !stack) return undefined;

    const apply = () => {
      stack.style.setProperty(
        "--mb-pills-width",
        `${Math.round(pills.getBoundingClientRect().width)}px`
      );
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(pills);
    return () => observer.disconnect();
  }, []);

  const goTestimonial = useCallback((direction) => {
    setTestimonialIndex((index) => {
      const next = direction === "next" ? index + 1 : index - 1;
      if (next < 0 || next >= TESTIMONIALS.length) return index;
      return next;
    });
  }, []);

  const setCursorStroke = useCallback((event) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--glow-x", `${event.clientX - rect.left}px`);
    el.style.setProperty("--glow-y", `${event.clientY - rect.top}px`);
  }, []);

  useEffect(() => {
    const page = document.querySelector(".page-full");
    [document.documentElement, document.body, page, document.querySelector(".page-wrapper")].forEach(
      (node) => {
        if (node) {
          node.style.backgroundColor = "";
        }
      }
    );
    page?.style.removeProperty("--mb-night");
    let frameId = 0;

    const updateNight = () => {
      const section = builtForRef.current;
      if (!section) return;
      const header = document.querySelector(".l2-header-bar");
      const headerBottom = header?.getBoundingClientRect().bottom ?? 64;
      const rect = section.getBoundingClientRect();
      const overlapsHeader = rect.top < headerBottom && rect.bottom > 0;
      page?.classList.toggle("page-full--night", overlapsHeader);
    };

    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        updateNight();
      });
    };

    updateNight();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.cancelAnimationFrame(frameId);
      page?.classList.remove("page-full--night");
    };
  }, []);

  useEffect(() => {
    const node = howCompareRef.current;
    if (!node) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      node.classList.add("is-in");
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        node.classList.add("is-in");
        observer.disconnect();
      },
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const node = howPlaceRef.current;
    if (!node) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      node.classList.add("is-in");
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        node.classList.add("is-in");
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (searchParams.get("brief") !== "1") return undefined;
    setBriefOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("brief");
    setSearchParams(next, { replace: true });
    return undefined;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!briefOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setBriefOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [briefOpen]);

  useEffect(() => {
    const heroCardCount = HERO_VENUE_PHOTOS.length * 2;
    const startMarqueeAt =
      (HERO_IMAGE_START +
        (heroCardCount - 1) * HERO_CARD_STAGGER +
        HERO_CARD_IN_DURATION) *
      1000;
    const timeoutId = window.setTimeout(() => setHeroMarquee(true), startMarqueeAt);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useLayoutEffect(() => {
    const strip = heroStripRef.current;
    const group = heroStripGroupRef.current;
    const track = strip?.querySelector(".mb-hero-strip-track");
    if (!strip || !group || !track) return undefined;

    const applyShift = () => {
      const shift = `${group.offsetWidth}px`;
      strip.style.setProperty("--mb-marquee-shift", shift);
      track.style.setProperty("--mb-marquee-shift", shift);
    };

    applyShift();
    const observer = new ResizeObserver(applyShift);
    observer.observe(group);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [heroMarquee]);

  useEffect(() => {
    const strip = heroStripRef.current;
    if (!strip || !heroMarquee) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const durationMs = 140000 / 1.3;
    let lastTime = 0;
    let dragging = false;
    let resumeTimer = 0;
    let programmatic = false;
    let bound = false;
    let pointerStartX = null;
    let position = strip.scrollLeft;

    const isMobile = () => window.innerWidth <= 767;
    const loopWidth = () => {
      const group = heroStripGroupRef.current;
      if (!group) return Math.round(strip.scrollWidth / 2);
      return group.offsetWidth;
    };

    const applyScroll = (left) => {
      const next = Math.round(left);
      if (next === Math.round(strip.scrollLeft)) return;
      programmatic = true;
      strip.scrollLeft = next;
      programmatic = false;
    };

    const wrapPosition = () => {
      const width = loopWidth();
      if (width <= 0) return;
      if (position >= width) position -= width;
      else if (position < 0) position += width;
    };

    const tick = (now) => {
      if (dragging || reduced.matches || !isMobile()) {
        lastTime = 0;
        return;
      }
      if (lastTime) {
        const width = loopWidth();
        if (width > 0) {
          position += (width / durationMs) * (now - lastTime);
          wrapPosition();
          applyScroll(position);
        }
      }
      lastTime = now;
    };

    const stopAuto = () => {
      dragging = true;
      lastTime = 0;
      window.clearTimeout(resumeTimer);
    };

    const resumeAuto = () => {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        dragging = false;
        lastTime = 0;
        position = strip.scrollLeft;
      }, 120);
    };

    const onScroll = () => {
      if (programmatic) return;
      position = strip.scrollLeft;
      wrapPosition();
      if (strip.scrollLeft !== position) applyScroll(position);
    };

    const eventX = (event) => event.clientX ?? event.touches?.[0]?.clientX;

    const onPointerDown = (event) => {
      pointerStartX = eventX(event);
    };

    const onPointerMove = (event) => {
      const x = eventX(event);
      if (pointerStartX == null || x == null) return;
      if (Math.abs(x - pointerStartX) < 12) return;
      stopAuto();
    };

    const onPointerUp = () => {
      pointerStartX = null;
      if (dragging) resumeAuto();
    };

    const bind = () => {
      if (bound || !isMobile()) return;
      bound = true;
      strip.addEventListener("scroll", onScroll, { passive: true });
      strip.addEventListener("pointerdown", onPointerDown);
      strip.addEventListener("pointermove", onPointerMove);
      strip.addEventListener("touchstart", onPointerDown, { passive: true });
      strip.addEventListener("touchmove", onPointerMove, { passive: true });
      strip.addEventListener("pointerup", onPointerUp);
      strip.addEventListener("pointercancel", onPointerUp);
      strip.addEventListener("lostpointercapture", onPointerUp);
      document.addEventListener("pointerup", onPointerUp, true);
      document.addEventListener("pointercancel", onPointerUp, true);
      document.addEventListener("touchend", onPointerUp, true);
      document.addEventListener("touchcancel", onPointerUp, true);
    };

    const unbind = () => {
      if (!bound) return;
      bound = false;
      strip.removeEventListener("scroll", onScroll);
      strip.removeEventListener("pointerdown", onPointerDown);
      strip.removeEventListener("pointermove", onPointerMove);
      strip.removeEventListener("touchstart", onPointerDown);
      strip.removeEventListener("touchmove", onPointerMove);
      strip.removeEventListener("pointerup", onPointerUp);
      strip.removeEventListener("pointercancel", onPointerUp);
      strip.removeEventListener("lostpointercapture", onPointerUp);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("pointercancel", onPointerUp, true);
      document.removeEventListener("touchend", onPointerUp, true);
      document.removeEventListener("touchcancel", onPointerUp, true);
      window.clearTimeout(resumeTimer);
      dragging = false;
      lastTime = 0;
      pointerStartX = null;
      programmatic = false;
    };

    const onModeChange = () => {
      if (isMobile()) bind();
      else unbind();
    };

    bind();
    window.addEventListener("resize", onModeChange);
    window.clearInterval(heroMobileMarqueeInterval);
    heroMobileMarqueeInterval = window.setInterval(() => tick(performance.now()), 16);
    return () => {
      window.removeEventListener("resize", onModeChange);
      unbind();
      window.clearInterval(heroMobileMarqueeInterval);
      heroMobileMarqueeInterval = 0;
    };
  }, [heroMarquee]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        FEATURED_VENUES.map(async (venue) => {
          try {
            const stats = await fetchFeaturedVenueStats(venue.id);
            return [venue.id, stats];
          } catch {
            return [
              venue.id,
              {
                price:
                  venue.id === KIJU_COFFEE_VENUE_ID
                    ? FEATURED_VENUE_CUSTOM_PRICING_LABEL
                    : FEATURED_VENUE_PRICE_FALLBACKS[venue.id] ?? null,
                capacity: null,
              },
            ];
          }
        })
      );

      if (!cancelled) {
        setFeaturedVenueStats(Object.fromEntries(entries));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateField = (key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const toggleInArrayField = (key, value) => {
    setFormState((prev) => {
      const current = prev[key];
      const exists = current.includes(value);
      return {
        ...prev,
        [key]: exists ? current.filter((item) => item !== value) : [...current, value],
      };
    });
  };

  const setSingleSelectArray = (key, value) => {
    setFormState((prev) => ({ ...prev, [key]: [value] }));
  };

  const canContinueStep1 = useMemo(() => isStep1Complete(formState), [formState]);
  const canContinueStep2 = useMemo(() => isStep2Complete(formState), [formState]);
  const canContinueStep3 = useMemo(() => isStep3Complete(formState), [formState]);
  const minEventDate = useMemo(() => formatLocalCalendarYmd(new Date()), []);

  const buildExtraNotes = () => {
    const parts = [];
    if (formState.bookingTimeSlot) {
      parts.push(`Preferred time slot: ${formState.bookingTimeSlot}`);
    }
    if (formState.mustHaves.length > 0) {
      parts.push(`Must haves: ${formState.mustHaves.join(", ")}`);
    }
    if (formState.additionalInfo.trim()) {
      parts.push(formState.additionalInfo.trim());
    }
    return parts.join("\n\n") || null;
  };

  const buildApiPayload = () => {
    const normalizedVibes = normalizeMultiSelect(
      formState.vibes.map(mapVibeToApi),
      VIBE_OPTIONS.map(mapVibeToApi).filter((v) => v !== OPEN_TO_ANYTHING)
    );
    const normalizedSpaceTypes = normalizeMultiSelect(
      formState.spaceTypes.map(mapSpaceTypeToApi),
      SPACE_TYPES.map(mapSpaceTypeToApi).filter((v) => v !== OPEN_TO_ANYTHING)
    );

    return {
      event_description: formState.planningDescription || null,
      event_type: formState.planningType || null,
      booking_frequency: mapBookingFrequencyToApi(formState.recurringPreference),
      first_date: formState.firstDate || null,
      date_flexibility: mapDateFlexibilityToApi(formState.flexibleDate),
      number_of_guests: formState.guestCount ? Number(formState.guestCount) : null,
      duration_needed: mapDurationToApi(formState.duration),
      venue_types: normalizedSpaceTypes,
      vibes: normalizedVibes,
      budget_range: mapBudgetToApi(formState.budgetRange),
      payment_preference: formState.pricingModel || "Not sure",
      target_area: formState.preferredArea.length ? formState.preferredArea.join(", ") : null,
      extra_notes: buildExtraNotes(),
      contact_name: formState.name || null,
      contact_email: formState.email || null,
      contact_phone: formState.phone ? `+44 ${formState.phone.replace(/^\+44\s*/, "").trim()}` : null,
      contact_preference: mapContactMethodToApi(formState.contactMethod) || null,
    };
  };

  const submitBrief = async () => {
    const response = await fetch(`${API_BASE_URL}/forms/venue-brief`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(buildApiPayload()),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }
    const responseData = await response.json().catch(() => null);
    return responseData?.id || responseData?.brief_id || null;
  };

  const patchBrief = async (briefId) => {
    const response = await fetch(`${API_BASE_URL}/forms/venue-brief/${briefId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(buildApiPayload()),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Update failed with status ${response.status}`);
    }
  };

  const handleSubmitEnquiry = async () => {
    if (!canContinueStep3 || isSubmitting) return;
    setSubmitError("");
    setIsSubmitting(true);
    try {
      if (submittedBriefId) {
        await patchBrief(submittedBriefId);
      } else {
        const briefId = await submitBrief();
        if (!briefId) {
          throw new Error("We could not create your brief. Please try again.");
        }
        setSubmittedBriefId(briefId);
        storeVenueBriefId(briefId);
      }
      setSubmittedAt((prev) => prev ?? Date.now());
      setBriefOpen(false);
      setPhase(QUESTION_COUNT > 0 ? "questionnaire" : "complete");
      setQuestionIndex(0);
      setCompletedQuestions(["vibe", "space", "mustHaves", "budget", "notes"]);
      setSkippedQuestions([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error?.message || "We could not send your enquiry right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormContinue = () => {
    if (formStep === 1) {
      if (!canContinueStep1) return;
      setFormStep(2);
      return;
    }
    if (formStep === 2) {
      if (!canContinueStep2) return;
      setFormStep(3);
      return;
    }
    handleSubmitEnquiry();
  };

  const handleFormBack = () => {
    if (formStep > 1) setFormStep((s) => s - 1);
  };

  const questionHasAnswer = () => {
    const qId = QUESTION_IDS[questionIndex];
    if (qId === "vibe") return formState.vibes.length > 0;
    if (qId === "mustHaves") return formState.mustHaves.length > 0;
    return false;
  };

  const isMandatoryQuestion = (questionId) => MANDATORY_QUESTION_IDS.has(questionId);
  const currentQuestionId = QUESTION_IDS[questionIndex];
  const canSkipCurrentQuestion = !isMandatoryQuestion(currentQuestionId);

  const finalizeBrief = async () => {
    if (isSubmitting || !submittedBriefId) {
      if (!submittedBriefId) {
        setSubmitError("Your brief was not found. Please go back and submit your enquiry again.");
      }
      return;
    }
    setSubmitError("");
    setIsSubmitting(true);
    try {
      await patchBrief(submittedBriefId);
      setPhase("complete");
    } catch (error) {
      setSubmitError(error?.message || "We could not save your brief right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToBrief = () => {
    if (isSubmitting || !questionHasAnswer()) return;
    const qId = QUESTION_IDS[questionIndex];
    if (isMandatoryQuestion(qId) && !questionHasAnswer()) return;

    setSubmitError("");
    setCompletedQuestions((prev) => (prev.includes(qId) ? prev : [...prev, qId]));
    setSkippedQuestions((prev) => prev.filter((id) => id !== qId));

    if (questionIndex >= QUESTION_COUNT - 1) {
      finalizeBrief();
      return;
    }

    setQuestionIndex((i) => i + 1);
  };

  const handleSkipQuestion = () => {
    const qId = QUESTION_IDS[questionIndex];
    if (isMandatoryQuestion(qId)) return;

    setSubmitError("");
    setSkippedQuestions((prev) => (prev.includes(qId) ? prev : [...prev, qId]));
    setCompletedQuestions((prev) => prev.filter((id) => id !== qId));

    if (questionIndex < QUESTION_COUNT - 1) {
      setQuestionIndex((i) => i + 1);
      return;
    }

    finalizeBrief();
  };

  const handleQuestionBack = () => {
    if (isSubmitting) return;
    setSubmitError("");

    if (questionIndex <= 0) {
      setPhase("form");
      setFormStep(3);
      setBriefOpen(true);
      return;
    }

    const newIndex = questionIndex - 1;
    const clearedIds = QUESTION_IDS.slice(newIndex);
    setQuestionIndex(newIndex);
    setCompletedQuestions((prev) => prev.filter((id) => !clearedIds.includes(id)));
    setSkippedQuestions((prev) => prev.filter((id) => !clearedIds.includes(id)));
  };

  const handleAccountSignIn = () => {
    saveAuthReturnTo("/profile");
    if (currentUser) {
      navigate("/profile");
      return;
    }
    triggerAuthModal();
  };

  const answeredCount = completedQuestions.length + skippedQuestions.length;
  const venueCount = VENUE_COUNTS[Math.min(answeredCount, VENUE_COUNTS.length - 1)];

  const summaryDateParts = [
    formatDateLabel(formState.firstDate),
    formState.flexibleDate,
    formatRecurringLabel(formState.recurringPreference),
  ].filter(Boolean);

  const summaryTimeParts = [formState.bookingTimeSlot, formState.duration].filter(Boolean);

  const renderQuestionKicker = () => (
    <div className="mb-question-kicker">
      <span>
        Question {questionIndex + 1} of {QUESTION_COUNT}
      </span>
      <span className="mb-question-kicker-dot" aria-hidden />
      <span className="mb-question-kicker-accent">Improves your matches</span>
    </div>
  );

  const renderPendingCard = () => {
    const facts = [
      { label: "Vibe", value: formState.vibes.join(", ") },
      { label: "Type of space", value: formState.spaceTypes.join(", ") },
      { label: "Hire budget", value: formState.budgetRange },
    ].filter((fact) => fact.value);
    const phone = formState.phone.trim();
    const contactFacts = [
      { label: "Name", value: formState.name.trim() },
      { label: "Email", value: formState.email.trim() },
      { label: "Phone", value: phone ? `+44 ${phone}` : "Not provided", muted: !phone },
      {
        label: "How we'll reach you",
        value: formState.contactMethod === "Either works" ? "Email or Whatsapp" : formState.contactMethod,
      },
    ];
    const metaLine = (icon, parts) => (
      <div className="mb-pending-meta-line">
        {icon}
        <span className="mb-pending-meta-text">
          {parts.map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 ? <span className="mb-pending-slash">/</span> : null}
              <span>{part}</span>
            </React.Fragment>
          ))}
        </span>
      </div>
    );

    return (
      <div className="mb-pending-card">
        <div className="mb-pending-head">
          <p className="mb-pending-head-title">
            <PendingIcon name="hourglass-orange" />
            Pending Requests
          </p>
          <BriefCountdown startedAt={submittedAt} />
        </div>

        <div className="mb-pending-body">
          <div className="mb-pending-event">
            <h2 className="mb-pending-title">{formState.planningType || "Event"}</h2>
            <div className="mb-pending-meta">
              {formState.preferredArea.length > 0
                ? metaLine(<PendingIcon name="location" />, formState.preferredArea)
                : null}
              {metaLine(<PendingIcon name="guests" />, [`${formState.guestCount} guests`])}
              {metaLine(<PendingIcon name="calendar" />, summaryDateParts)}
              {metaLine(<PendingIcon name="clock" />, summaryTimeParts)}
            </div>
          </div>

          <hr className="mb-pending-rule" />

          {facts.length > 0 ? (
            <dl className="mb-pending-facts">
              {facts.map((fact) => (
                <div key={fact.label} className="mb-pending-fact">
                  <dt className="mb-pending-label">{fact.label}</dt>
                  <dd className="mb-pending-value">{fact.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          <div className="mb-pending-fact">
            <p className="mb-pending-label">Any other information</p>
            <p className="mb-pending-text">{formState.additionalInfo.trim() || "None"}</p>
          </div>

          {formState.mustHaves.length > 0 ? (
            <div className="mb-pending-fact">
              <p className="mb-pending-label">Must haves</p>
              <div className="mb-pending-chips">
                {formState.mustHaves.map((item) => (
                  <span key={item} className="mb-pending-chip">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <hr className="mb-pending-rule" />

          <dl className="mb-pending-facts mb-pending-facts--contact">
            {contactFacts.map((fact) => (
              <div key={fact.label} className="mb-pending-fact">
                <dt className="mb-pending-label">{fact.label}</dt>
                <dd className={`mb-pending-value${fact.muted ? " mb-pending-value--muted" : ""}`}>{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mb-pending-status">
          <div className="mb-pending-status-copy">
            <p className="mb-pending-status-title">Reaching out to venues now</p>
            <p className="mb-pending-status-lead">
              We&apos;re contacting best matches for you. We&apos;ll update you as soon as we have a response.{" "}
              <strong>Usually within 24 hours</strong>
            </p>
          </div>
          <img
            className="mb-pending-status-illus"
            src="/manual-booking/reaching-out-plane-3x.png"
            alt=""
            aria-hidden
            width="102"
            height="59"
          />
        </div>
      </div>
    );
  };

  const renderQuestionContent = () => {
    const qId = QUESTION_IDS[questionIndex];
    if (qId === "vibe") {
      return (
        <>
          {renderQuestionKicker()}
          <div className="mb-question-heading">
            <h3 className="mb-question-title">What&apos;s the vibe?</h3>
            <p className="mb-question-subtitle">Help us filter out spaces that would feel right for your event</p>
          </div>
          <div className="mb-pill-row">
            {VIBE_OPTIONS.map((label) => (
              <button
                key={label}
                type="button"
                className={`mb-pill ${formState.vibes.includes(label) ? "mb-pill--selected" : ""}`}
                onClick={() => setSingleSelectArray("vibes", label)}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      );
    }

    return (
      <>
        {renderQuestionKicker()}
        <div className="mb-question-heading">
          <h3 className="mb-question-title">Must haves?</h3>
          <p className="mb-question-subtitle">
            Select anything that&apos;s a dealbreaker. We&apos;ll filter hard on these
          </p>
        </div>
        <div className="mb-pill-row">
          {MUST_HAVE_OPTIONS.map((label) => (
            <button
              key={label}
              type="button"
              className={`mb-pill ${formState.mustHaves.includes(label) ? "mb-pill--selected" : ""}`}
              onClick={() => toggleInArrayField("mustHaves", label)}
            >
              {label}
            </button>
          ))}
        </div>
      </>
    );
  };

  return (
    <PageWrapper
      useNewFooter
      useLanding2Header
      showPersonaSwitcher={phase === "form"}
      persona="events"
      onPersonaChange={(nextPersona) => {
        if (nextPersona === "venues") {
          navigate("/onboard?persona=venues");
        }
      }}
    >
      <main className={`manual-booking-page ${phase === "form" ? "manual-booking-page--landing" : ""}`}>
        {phase === "form" ? (
          <>
            <section className="mb-hero">
              <div className="mb-hero-inner">
                <div className="mb-hero-copy">
                  <h1 className="mb-hero-title">
                    {HERO_TITLE_LINES.map((line, lineIndex) => {
                      const wordOffset = HERO_TITLE_LINES.slice(0, lineIndex).reduce(
                        (count, item) => count + item.words.length,
                        0
                      );
                      return (
                        <span
                          key={line.words.join("-")}
                          className={`mb-hero-title-line${line.muted ? "" : " mb-hero-highlight"}`}
                        >
                          {line.words.map((word, wordIndex) => (
                            <span
                              key={`${word}-${wordIndex}`}
                              className="mb-hero-word"
                              style={{
                                animationDelay: `${HERO_TITLE_WORD_START + (wordOffset + wordIndex) * HERO_TITLE_WORD_STAGGER}s`,
                              }}
                            >
                              {word}
                            </span>
                          ))}
                        </span>
                      );
                    })}
                  </h1>
                  <p className="mb-hero-subtitle">
                    Send us a brief and we&apos;ll come back with confirmed venues,
                    <br />
                    real pricing, and no back-and-forth.
                  </p>
                  <div className="mb-hero-note-row">
                    <span className="mb-hero-note">
                      <img
                        src={ICON("profile-fill.png")}
                        alt=""
                        className="mb-hero-note-icon"
                        aria-hidden
                      />
                      No signup needed
                    </span>
                    <span className="mb-hero-note-sep" aria-hidden>
                      ·
                    </span>
                    <span className="mb-hero-note">
                      <img
                        src={ICON("timer.png")}
                        alt=""
                        className="mb-hero-note-icon"
                        aria-hidden
                      />
                      Takes under 2 minutes
                    </span>
                  </div>
                  <div className="mb-hero-cta-row">
                    <button type="button" className="mb-hero-cta" onClick={() => setBriefOpen(true)}>
                      Start your venue brief
                    </button>
                  </div>
                </div>
              </div>
              <div
                className={`mb-hero-strip${heroMarquee ? " mb-hero-strip--marquee" : ""}`}
                ref={heroStripRef}
                aria-hidden
              >
                <div className="mb-hero-strip-track">
                  <div className="mb-hero-strip-group" ref={heroStripGroupRef}>
                    {HERO_VENUE_PHOTOS.map((src, i) => (
                      <div
                        key={`hero-card-${i}`}
                        className="mb-hero-strip-card"
                        style={{
                          animationDelay: `${HERO_IMAGE_START + i * HERO_CARD_STAGGER}s`,
                        }}
                      >
                        <img src={src} alt="" />
                      </div>
                    ))}
                  </div>
                  <div className="mb-hero-strip-group mb-hero-strip-group--clone">
                    {HERO_VENUE_PHOTOS.map((src, i) => (
                      <div
                        key={`hero-card-clone-${i}`}
                        className="mb-hero-strip-card mb-hero-strip-card--clone"
                        style={{
                          animationDelay: `${HERO_IMAGE_START + (HERO_VENUE_PHOTOS.length + i) * HERO_CARD_STAGGER}s`,
                        }}
                      >
                        <img src={src} alt="" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {briefOpen ? (
              <div className="mb-brief-drawer" role="presentation">
                <div className="mb-brief-grid" aria-hidden>
                  <canvas ref={briefGridRef} className="mb-testimonials-grid-dots" />
                </div>
                <aside
                  className="mb-brief-drawer-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Venue brief"
                  onScroll={(e) => setBriefScrolled(e.currentTarget.scrollTop > 8)}
                >
                <div className={`mb-brief-page-header l2-header-bar ${briefScrolled ? "l2-header-bar--scrolled" : ""}`}>
                  <nav className="l2-header-nav">
                    <Link
                      to="/"
                      className="l2-header-logo"
                      aria-label="Occasia home"
                      onClick={() => setBriefOpen(false)}
                    >
                      <OccasiaLogoIcon className="l2-header-logo-svg" aria-label="Occasia" />
                    </Link>
                    <div className="mb-brief-page-tools">
                      <AskHelpButton />
                      <CloseButton
                        className="mb-brief-page-close"
                        onClick={() => setBriefOpen(false)}
                      />
                    </div>
                  </nav>
                </div>
                <div className="mb-brief-frame">
                <span className="mb-testimonials-frame-rule mb-testimonials-frame-rule--top" aria-hidden />
                <span className="mb-testimonials-frame-rule mb-testimonials-frame-rule--bottom" aria-hidden />
                <span className="mb-testimonials-corner mb-testimonials-corner--tl" aria-hidden />
                <span className="mb-testimonials-corner mb-testimonials-corner--tr" aria-hidden />
                <span className="mb-testimonials-corner mb-testimonials-corner--bl" aria-hidden />
                <span className="mb-testimonials-corner mb-testimonials-corner--br" aria-hidden />
                <div className="mb-form-card">
                  <Stepper currentStep={formStep} />
                  {formStep === 1 ? (
                    <>
                      <div className="mb-form-heading">
                        <h2 className="mb-form-title">What are you planning?</h2>
                        <p className="mb-form-subtitle">A few details about your event so we can shortlist the right spaces.</p>
                      </div>

                      <div className="mb-grid-2">
                        <div className="mb-field-block mb-field-block--compact">
                          <FieldLabel htmlFor="mb-area">Location/area</FieldLabel>
                          <BriefSelect
                            id="mb-area"
                            label="Location/area"
                            multiple
                            value={formState.preferredArea}
                            options={LOCATION_AREAS}
                            onChange={(value) => updateField("preferredArea", value)}
                            describedBy="mb-area-hint"
                          />
                          <p id="mb-area-hint" className="mb-field-help">You can select more than one location preference.</p>
                        </div>
                        <div className="mb-field-block mb-field-block--compact">
                          <FieldLabel htmlFor="mb-type">Event type</FieldLabel>
                          <BriefSelect
                            id="mb-type"
                            label="Event type"
                            value={formState.planningType}
                            options={EVENT_TYPES}
                            onChange={(value) => updateField("planningType", value)}
                          />
                        </div>
                      </div>

                      <div className="mb-grid-date">
                        <div className="mb-field-block mb-field-block--compact">
                          <FieldLabel htmlFor="mb-date">Event date</FieldLabel>
                          <CustomDatePicker
                            id="mb-date"
                            className="mb-date-picker"
                            dropdownClassName="mb-date-menu"
                            matchInputWidth
                            mobileSheet
                            alignHeaderToGrid
                            value={formState.firstDate}
                            onChange={(date) => updateField("firstDate", date)}
                            placeholder="Select date"
                            minDate={minEventDate}
                          />
                        </div>
                        <div className="mb-field-block mb-field-block--compact">
                          <FieldLabel htmlFor="mb-guests">How many guests?</FieldLabel>
                          <div className="mb-guest-stepper">
                            <input
                              id="mb-guests"
                              type="number"
                              min="1"
                              className={`mb-input ${!formState.guestCount ? "mb-input--placeholder" : ""}`}
                              value={formState.guestCount}
                              onChange={(e) => updateField("guestCount", e.target.value)}
                              placeholder="No. of guests"
                            />
                            <div className="mb-guest-stepper-actions">
                              <button
                                type="button"
                                className="mb-guest-stepper-btn"
                                aria-label="Decrease guests"
                                disabled={!formState.guestCount || Number(formState.guestCount) <= 1}
                                onClick={() =>
                                  updateField("guestCount", String(Math.max(1, Number(formState.guestCount) - 1)))
                                }
                              >
                                <GuestMinusIcon />
                              </button>
                              <button
                                type="button"
                                className="mb-guest-stepper-btn"
                                aria-label="Increase guests"
                                onClick={() =>
                                  updateField(
                                    "guestCount",
                                    String((Number(formState.guestCount) || 0) + 1)
                                  )
                                }
                              >
                                <GuestPlusIcon />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-field-block">
                        <span className="mb-field-label">
                          When do you need the booking?<span className="required"> *</span>
                        </span>
                        <div className="mb-checkbox-row">
                          {BOOKING_TIME_SLOTS.map((slot) => (
                            <FigmaCheckbox
                              key={slot}
                              label={slot}
                              checked={formState.bookingTimeSlot === slot}
                              onChange={() => updateField("bookingTimeSlot", slot)}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="mb-field-block">
                        <span className="mb-field-label">
                          How long?<span className="required"> *</span>
                        </span>
                        <div className="mb-checkbox-row mb-checkbox-row--duration">
                          {DURATION_OPTIONS.map((opt) => (
                            <FigmaCheckbox
                              key={opt.value}
                              label={opt.label}
                              sub={opt.sub}
                              checked={formState.duration === opt.value}
                              onChange={() => updateField("duration", opt.value)}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="mb-radio-pair">
                        <div className="mb-field-block">
                          <FieldLabel>Flexibility</FieldLabel>
                          <div className="mb-radio-row" role="radiogroup" aria-label="Flexibility">
                            {FLEXIBLE_DATE_OPTIONS.map((opt) => (
                              <FigmaRadio
                                key={opt}
                                label={opt}
                                checked={formState.flexibleDate === opt}
                                onChange={() => updateField("flexibleDate", opt)}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="mb-field-block">
                          <FieldLabel>Booking frequency</FieldLabel>
                          <div className="mb-radio-row" role="radiogroup" aria-label="Booking frequency">
                            {RECURRING_OPTIONS.map((opt) => (
                              <FigmaRadio
                                key={opt}
                                label={opt}
                                checked={formState.recurringPreference === opt}
                                onChange={() => updateField("recurringPreference", opt)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : formStep === 2 ? (
                    <>
                      <div className="mb-form-heading">
                        <h2 className="mb-form-title">Your budget and the space you need</h2>
                        <p className="mb-form-subtitle">These details help us shortlist the right venues for you</p>
                      </div>

                      <div className="mb-field-block mb-field-block--compact">
                        <BudgetSlider
                          id="mb-budget"
                          label={<FieldLabel htmlFor="mb-budget">What&apos;s your hire budget?</FieldLabel>}
                          value={formState.budgetRange}
                          onChange={(value) => updateField("budgetRange", value)}
                          note={
                            <div className="mb-budget-note">
                              <strong>Note:</strong> General rule of thumb is to account for £7-10 / person
                            </div>
                          }
                        />
                      </div>

                      <div className="mb-field-block mb-field-block--compact">
                        <FieldLabel htmlFor="mb-vibe">What&apos;s the vibe?</FieldLabel>
                        <BriefSelect
                          id="mb-vibe"
                          label="What's the vibe?"
                          value={formState.vibes[0] || ""}
                          options={VIBE_OPTIONS}
                          onChange={(value) => setSingleSelectArray("vibes", value)}
                          describedBy="mb-vibe-hint"
                        />
                        <p id="mb-vibe-hint" className="mb-field-help">Help us filter out spaces that would feel right for your event</p>
                      </div>

                      <div className="mb-field-block mb-field-block--compact">
                        <FieldLabel htmlFor="mb-space">Type of space</FieldLabel>
                        <BriefSelect
                          id="mb-space"
                          label="Type of space"
                          value={formState.spaceTypes[0] || ""}
                          options={SPACE_TYPES}
                          onChange={(value) => setSingleSelectArray("spaceTypes", value)}
                          describedBy="mb-space-hint"
                        />
                        <p id="mb-space-hint" className="mb-field-help">Select the venue type that best fits your event</p>
                      </div>

                      <div className="mb-field-block">
                        <label className="mb-field-label" htmlFor="mb-additional-info">
                          Anything the venue should know?<span className="required"> *</span>
                        </label>
                        <textarea
                          id="mb-additional-info"
                          aria-describedby="mb-additional-info-hint"
                          className="mb-notes-input"
                          rows={3}
                          value={formState.additionalInfo}
                          onChange={(e) => updateField("additionalInfo", e.target.value)}
                          placeholder="e.g We're a corporate group looking for relevant prices for our official event"
                          required
                          aria-required="true"
                        />
                        <p id="mb-additional-info-hint" className="mb-field-help">
                          Accessibility needs, catering policies, overtime flexibility - anything relevant.
                        </p>
                      </div>

                      <div className="mb-field-block mb-field-block--compact">
                        <label className="mb-field-label" htmlFor="mb-must-haves" id="mb-must-haves-label">
                          Must haves (optional)
                        </label>
                        <BriefSelect
                          id="mb-must-haves"
                          label="Must haves"
                          multiple
                          value={formState.mustHaves}
                          options={MUST_HAVE_OPTIONS}
                          onChange={(value) => updateField("mustHaves", value)}
                          describedBy="mb-must-haves-hint"
                          placeholder="Select any must haves"
                        />
                        <p id="mb-must-haves-hint" className="mb-field-help">
                          Select anything that&apos;s a dealbreaker. We&apos;ll filter hard on these
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-form-heading">
                        <h2 className="mb-form-title">How can we reach you?</h2>
                        <p className="mb-form-subtitle">We&apos;ll send your venue matches here</p>
                      </div>

                      <div className="mb-field-block">
                        <label className="mb-field-label" htmlFor="mb-name">
                          Your name<span className="required"> *</span>
                        </label>
                        <input
                          id="mb-name"
                          type="text"
                          className="mb-input"
                          value={formState.name}
                          onChange={(e) => updateField("name", e.target.value)}
                          placeholder="John Doe"
                        />
                      </div>

                      <div className="mb-input-row">
                        <div className="mb-field-block">
                          <label className="mb-field-label" htmlFor="mb-email">
                            Email<span className="required"> *</span>
                          </label>
                          <input
                            id="mb-email"
                            type="email"
                            className="mb-input"
                            value={formState.email}
                            onChange={(e) => updateField("email", e.target.value)}
                            placeholder="johndoe@gmail.com"
                          />
                        </div>
                        <div className="mb-field-block">
                          <label className="mb-field-label" htmlFor="mb-phone">
                            Phone (optional)
                          </label>
                          <div className="mb-phone-row">
                            <span className="mb-phone-prefix">🇬🇧 +44</span>
                            <input
                              id="mb-phone"
                              type="tel"
                              className="mb-phone-input"
                              value={formState.phone}
                              onChange={(e) => updateField("phone", e.target.value)}
                              placeholder="20 7123 4567"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mb-field-block">
                        <span className="mb-field-label">
                          How should we reach you?<span className="required"> *</span>
                        </span>
                        <div className="mb-pill-row">
                          {CONTACT_METHOD_OPTIONS.map((label) => (
                            <button
                              key={label}
                              type="button"
                              className={`mb-pill ${formState.contactMethod === label ? "mb-pill--selected" : ""}`}
                              onClick={() => updateField("contactMethod", label)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                </div>
                <div className="mb-form-footer">
                  {submitError ? <p className="mb-error">{submitError}</p> : null}
                  <div className={`mb-form-nav ${formStep === 1 ? "mb-form-nav--end" : ""}`}>
                    {formStep > 1 ? (
                      <button type="button" className="mb-btn-back" onClick={handleFormBack}>
                        <ArrowLeft />
                        Back
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="mb-btn-primary"
                      disabled={
                        (formStep === 1
                          ? !canContinueStep1
                          : formStep === 2
                            ? !canContinueStep2
                            : !canContinueStep3) || isSubmitting
                      }
                      onClick={handleFormContinue}
                    >
                      {formStep === 3
                        ? isSubmitting
                          ? "Submitting enquiry…"
                          : "Submit enquiry"
                        : "Next"}
                      <ArrowRight />
                    </button>
                  </div>
                </div>
                </div>
                </aside>
              </div>
            ) : null}

            <section className="mb-how-it-works">
              <div className="mb-how-it-works-inner">
                <div className="mb-how-header">
                  <p className="mb-how-kicker">HOW IT WORKS</p>
                  <h2 className="mb-how-title">
                    <span className="mb-how-title-line">
                      No search.{' '}
                      <br className="mb-how-title-mobile-break" />
                      No back-and-forth.
                    </span>
                    <span className="mb-how-title-line mb-hero-highlight">A confirmed venue.</span>
                  </h2>
                  <p className="mb-how-lead">Submit a brief. We handle everything else.</p>
                </div>
                <div className="mb-how-steps">
                  {HOW_IT_WORKS_STEPS.map((step) => (
                      <div className="mb-how-step" key={step.title}>
                        <img src={step.icon} alt="" className="mb-how-step-icon" aria-hidden />
                        <div className="mb-how-step-rule" aria-hidden />
                        <div className="mb-how-step-copy">
                          <h3 className="mb-how-step-title">{step.title}</h3>
                          <p className="mb-how-step-desc">{step.desc}</p>
                        </div>
                        <p className="mb-how-step-pill">{step.pill}</p>
                      </div>
                  ))}
                </div>
                <div className="mb-how-compare-block">
                <h2 className="mb-how-compare-heading">
                  <span className="mb-how-compare-heading-line">Why choose Occasia?</span>
                </h2>
                <div
                  className="mb-how-compare"
                  ref={howCompareRef}
                  role="group"
                  aria-label="How Occasia compares to manual sourcing"
                >
                  {HOW_IT_WORKS_COMPARE.map((graph) => (
                    <div className="mb-how-compare-col" key={graph.title}>
                      <div className="mb-how-compare-copy">
                        <h3 className="mb-how-compare-title">{graph.title}</h3>
                        <p className="mb-how-compare-desc">
                          {graph.traditionalCopy}
                          <br />
                          {graph.nowCopy}
                        </p>
                      </div>
                      <div className="mb-how-compare-chart">
                        <div className="mb-how-compare-row">
                          <div
                            className="mb-how-compare-bar mb-how-compare-bar--traditional"
                            style={{ "--bar-width": graph.traditionalWidth }}
                            aria-hidden
                          />
                          <p className="mb-how-compare-label mb-how-compare-label--traditional">
                            <span>Manual</span>
                            <strong>{graph.traditionalValue}</strong>
                          </p>
                        </div>
                        <div className="mb-how-compare-row">
                          <div
                            className="mb-how-compare-bar mb-how-compare-bar--now"
                            style={{ "--bar-width": graph.nowWidth }}
                            aria-hidden
                          />
                          <p className="mb-how-compare-label mb-how-compare-label--now">
                            <span>Occasia</span>
                            <strong>{graph.nowValue}</strong>
                          </p>

                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>
              <div className="mb-how-place" ref={howPlaceRef}>
                <div className="mb-how-place-rings" aria-hidden>
                  <span className="mb-how-place-ring" />
                  <span className="mb-how-place-ring" />
                  <span className="mb-how-place-ring" />
                  <span className="mb-how-place-ring" />
                  <span className="mb-how-place-ring" />
                </div>
                <div className="mb-how-place-copy">
                <p className="mb-how-place-roll" aria-label="Book, Pay, Plan, Manage">
                  <span className="mb-how-place-roll-word">Book</span>
                  <span className="mb-how-place-roll-word">Pay</span>
                  <span className="mb-how-place-roll-word">Plan</span>
                  <span className="mb-how-place-roll-word">Manage</span>
                </p>
                <h2 className="mb-how-place-title">
                  Everything your event needs,
                  <br />
                  in one place
                </h2>
                </div>
              </div>
            </section>

            <section
              className="mb-built-for"
              ref={builtForRef}
            >
              <div className="mb-built-for-media" aria-hidden>
                {BUILT_FOR_SCENES.map((scene, index) => (
                  <img
                    key={scene.image}
                    src={scene.image}
                    alt=""
                    className={builtForScene === index ? "is-active" : ""}
                  />
                ))}
                <div className="mb-built-for-media-fade" />
              </div>
              <div className="mb-built-for-stack">
                <div className="mb-built-for-inner">
                  <h2 className="mb-hero-title">
                    <span className="mb-hero-title-line mb-hero-highlight">
                      From supper clubs to
                      <br />
                      small team socials
                    </span>
                  </h2>
                  <p className="mb-hero-subtitle">
                    <span className="mb-built-for-subtitle-block">
                      Occasia is for people running small events without an agency or events team
                      behind them.
                    </span>
                    <span className="mb-built-for-subtitle-block">
                      <strong>Tell us what you&apos;re planning and when.</strong>
                    </span>
                    <span className="mb-built-for-subtitle-block">
                      We&apos;ll match you with an independent London venue and stay with you until
                      the booking is confirmed
                    </span>
                  </p>
                  <div className="mb-hero-cta-row">
                    <button
                      type="button"
                      className="mb-hero-cta"
                      onClick={() =>
                        document.querySelector(".mb-hero")?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      Find a venue
                    </button>
                  </div>
                </div>
                <div className="mb-built-for-stage">
                  <div className="mb-built-for-card">
                    <p className="mb-built-for-card-body">{BUILT_FOR_SCENES[builtForScene].name}</p>
                    <p className="mb-built-for-card-eyebrow">
                      {BUILT_FOR_SCENES[builtForScene].eyebrow}
                    </p>
                  </div>
                  <div className="mb-built-for-pills-block">
                    <p className="mb-built-for-pills-kicker">
                      Events you can find a venue for{' '}
                      <span className="mb-built-for-pills-kicker-tail">with Occasia</span>
                    </p>
                    <div className="mb-built-for-pills" ref={builtForPillsRef}>
                    {BUILT_FOR_SCENES.map((scene, index) => (
                      <button
                        key={scene.event}
                        type="button"
                        className={`mb-built-for-pill${builtForScene === index ? " is-active" : ""}`}
                        onClick={() => setBuiltForScene(index)}
                      >
                        {scene.event}
                      </button>
                    ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-built-for-ticker" aria-hidden>
                <div className="mb-built-for-ticker-track">
                  {[0, 1].map((copy) => (
                    <div className="mb-built-for-ticker-group" key={copy}>
                      {BUILT_FOR_EVENT_TYPES.map((eventType) => (
                        <span key={`${copy}-${eventType}`} className="mb-built-for-ticker-item">
                          {eventType}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-testimonials">
              <div className="mb-testimonials-grid" aria-hidden>
                <canvas ref={testimonialsGridRef} className="mb-testimonials-grid-dots" />
              </div>
              <div className="mb-testimonials-inner">
                <div className="mb-testimonials-header">
                  <p
                    className="mb-how-place-roll mb-how-place-roll--3"
                    aria-label="Real organisers, Real venues, Real community moments"
                  >
                    <span className="mb-how-place-roll-word">Real organisers</span>
                    <span className="mb-how-place-roll-word">Real venues</span>
                    <span className="mb-how-place-roll-word">Real community moments</span>
                  </p>
                  <h2 className="mb-testimonials-title">
                    Events that found their
                    <br />
                    perfect space
                  </h2>
                </div>
                <div className="mb-testimonials-carousel">
                  <div className="mb-testimonials-frame">
                    <span className="mb-testimonials-frame-rule mb-testimonials-frame-rule--top" aria-hidden />
                    <span className="mb-testimonials-frame-rule mb-testimonials-frame-rule--bottom" aria-hidden />
                    <span className="mb-testimonials-corner mb-testimonials-corner--tl" aria-hidden />
                    <span className="mb-testimonials-corner mb-testimonials-corner--tr" aria-hidden />
                    <button
                      type="button"
                      className="mb-testimonials-nav mb-testimonials-nav--prev"
                      aria-label="Previous testimonial"
                      disabled={testimonialIndex === 0}
                      onClick={() => goTestimonial("prev")}
                    >
                      <img src={ICON("arrow-left.svg")} alt="" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="mb-testimonials-nav mb-testimonials-nav--next"
                      aria-label="Next testimonial"
                      disabled={testimonialIndex === TESTIMONIALS.length - 1}
                      onClick={() => goTestimonial("next")}
                    >
                      <img src={ICON("arrow-right-nav.svg")} alt="" aria-hidden />
                    </button>
                    <div className="mb-testimonials-track">
                      <div className="mb-testimonial-quotes">
                        {TESTIMONIALS.map((item, index) => (
                          <p
                            key={item.author}
                            className={`mb-testimonial-quote${index === testimonialIndex ? " is-active" : ""}`}
                          >
                            &ldquo;{item.quote}&rdquo;
                          </p>
                        ))}
                      </div>
                      <div className="mb-testimonial-faces">
                        <div
                          className="mb-testimonial-faces-track"
                          style={{
                            transform: `translateX(calc(var(--mb-face-slot) * ${1 - testimonialIndex}))`,
                          }}
                        >
                          {TESTIMONIALS.map((item, index) => (
                            <button
                              key={item.author}
                              type="button"
                              className={`mb-testimonial-avatar-wrap${index === testimonialIndex ? " is-active" : ""}`}
                              aria-label={`${item.author}’s testimonial`}
                              aria-pressed={index === testimonialIndex}
                              onClick={() => setTestimonialIndex(index)}
                            >
                              <img
                                src={item.avatar}
                                alt=""
                                className={`mb-testimonial-avatar${index === testimonialIndex ? " is-active" : ""}`}
                                style={item.avatarPosition ? { objectPosition: item.avatarPosition } : undefined}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mb-testimonial-bylines">
                        {TESTIMONIALS.map((item, index) => (
                          <div
                            key={item.author}
                            className={`mb-testimonial-details${index === testimonialIndex ? " is-active" : ""}`}
                          >
                            <p className="mb-testimonial-author">{item.author}</p>
                            <h3 className="mb-testimonial-event">{item.event}</h3>
                            {item.meta ? <p className="mb-testimonial-meta">{item.meta}</p> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-trusted-venues">
              <div className="mb-trusted-venues-inner">
                <h2 className="mb-trusted-venues-title">Spaces worth showing up to</h2>
                <p className="mb-trusted-venues-lead">
                  Venues across London chosen for the events people actually want to attend.
                </p>
                <div className="mb-trusted-venues-list">
                  {FEATURED_VENUES.map((venue) => {
                    const liveStats = featuredVenueStats[venue.id];
                    return (
                      <article
                        key={venue.name}
                        className="mb-trusted-venue mb-cursor-stroke"
                        onMouseMove={setCursorStroke}
                      >
                        <div className="mb-trusted-venue-image-wrap">
                          <img
                            src={liveStats?.image || venue.image}
                            alt={venue.name}
                            className="mb-trusted-venue-image"
                          />
                        </div>
                        <div className="mb-trusted-venue-body">
                          <div className="mb-trusted-venue-head">
                            <h3 className="mb-trusted-venue-name">
                              <Link to={venue.path}>{venue.name}</Link>
                            </h3>
                            <Link
                              to={venue.path}
                              className="mb-trusted-venue-arrow-link"
                              aria-label={`View ${venue.name}`}
                            >
                              <img src={ICON("arrow-right-card.png")} alt="" className="mb-trusted-venue-arrow" aria-hidden />
                            </Link>
                          </div>
                          <div className="mb-trusted-venue-meta">
                            <span className="mb-trusted-venue-capacity">
                              <img src={ICON("profile-2user.png")} alt="" aria-hidden />
                              {liveStats?.capacity ? `Up to ${liveStats.capacity} guests` : "…"}
                            </span>
                          </div>
                          <div className="mb-trusted-venue-row">
                            <img src={ICON("tube.svg")} alt="" aria-hidden />
                            <span>{venue.transit}</span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                <VenuesBanner variant="landing" />
              </div>
            </section>

            <section className="mb-faq">
              <div className="mb-faq-inner">
                <h2 className="mb-how-title mb-faq-title">
                  <span className="mb-how-title-line">Got questions?</span>
                  <span className="mb-how-title-line mb-hero-highlight">We&apos;ve got answers</span>
                </h2>
                <div className="mb-faq-panel">
                  <div className="mb-faq-rule" aria-hidden />
                  <div className="mb-faq-list">
                    {HOMEPAGE_FAQS.map((item) => (
                      <details key={item.q} className="mb-faq-item">
                        <summary className="mb-faq-question">
                          {item.q}
                          <img src={ICON("faq-add.png")} alt="" className="mb-faq-icon mb-faq-icon--closed" aria-hidden />
                          <img src={ICON("faq-minus.png")} alt="" className="mb-faq-icon mb-faq-icon--open" aria-hidden />
                        </summary>
                        <p className="mb-faq-answer">{item.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : null}

        {phase === "questionnaire" || phase === "complete" ? (
          <section className="mb-outreach">
            <div className="mb-brief-grid" aria-hidden>
              <canvas ref={outreachGridRef} className="mb-testimonials-grid-dots" />
            </div>
            <div className="mb-outreach-inner">
              <h1 className="mb-outreach-thanks">
                <span className="mb-outreach-thanks-muted">
                  {formState.name.trim() ? "Thank you," : "Thank you"}
                </span>
                {formState.name.trim() ? <span className="mb-outreach-thanks-name">{formState.name.trim()}</span> : null}
              </h1>
              {renderPendingCard()}

              {phase === "questionnaire" ? (
                <div className="mb-brief-card">
                  <div className="mb-question-section">
                    {renderQuestionContent()}
                    <div className="mb-question-actions">
                      <button
                        type="button"
                        className="mb-btn-back"
                        disabled={isSubmitting}
                        onClick={handleQuestionBack}
                      >
                        <ArrowLeft />
                        Back
                      </button>
                      <div className="mb-question-actions-right">
                        {canSkipCurrentQuestion ? (
                          <button
                            type="button"
                            className="mb-link-skip"
                            disabled={isSubmitting}
                            onClick={handleSkipQuestion}
                          >
                            Skip this one
                          </button>
                        ) : (
                          <span className="mb-link-skip mb-link-skip--required">Required</span>
                        )}
                        <button
                          type="button"
                          className="mb-btn-add"
                          disabled={isSubmitting || !questionHasAnswer()}
                          onClick={handleAddToBrief}
                        >
                          {isSubmitting ? (
                            questionIndex >= QUESTION_COUNT - 1 ? "Submitting enquiry…" : "Completing…"
                          ) : questionIndex >= QUESTION_COUNT - 1 ? (
                            "Submit enquiry"
                          ) : (
                            <>
                              <AddIcon />
                              Add to brief
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mb-venue-count">
                    <BubbleIcon />
                    {venueCount} potential venues to curate from...
                  </div>
                </div>
              ) : null}

              {phase === "questionnaire" && submitError ? (
                <p className="mb-error mb-error--outreach">{submitError}</p>
              ) : null}

              {phase === "complete" ? (
                <>
                  {!currentUser ? (
                  <div className="mb-account-card">
                    <div className="mb-account-card-top">
                      <img
                        className="mb-account-illus"
                        src="/manual-booking/account-illustration.png"
                        alt=""
                        aria-hidden
                      />
                      <button type="button" className="mb-account-signin" onClick={handleAccountSignIn}>
                        Sign in
                      </button>
                    </div>
                    <h2 className="mb-account-title">Let&apos;s set up your account. It&apos;s free!</h2>
                    <p className="mb-account-sub">
                      Everything you need to plan better events, in one place.{" "}
                      <strong>Create your account to get started.</strong>
                    </p>
                    <div className="mb-account-features">
                      <p className="mb-account-features-label">What you get</p>
                      <div className="mb-account-features-grid">
                        {[
                          "Manage all your bookings in one place",
                          "Handle multiple events simultaneously",
                          "Message venue owners directly",
                          "Build and share venue shortlists",
                          "Discover new and trending spaces",
                        ].map((text) => (
                          <div key={text} className="mb-account-feature">
                            <StarFeatureIcon />
                            {text}
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="mb-account-signin mb-account-signin--block"
                      onClick={handleAccountSignIn}
                    >
                      Sign in
                    </button>
                  </div>
                  ) : null}
                  {submitError ? <p className="mb-error">{submitError}</p> : null}
                </>
              ) : null}
            </div>
          </section>
        ) : null}
      </main>
    </PageWrapper>
  );
};

export default ManualBooking;
